import { Box, styled, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, DataGridPro, DataGridProProps, PropsFromSlot, Toolbar, ToolbarButton } from "@mui/x-data-grid-pro";
import { useContext, useMemo } from "react";
import { AppContext } from "../../../../../../common/contexts/app_context/app_context";
import { useColumnSelectionConfig } from "../../../../../../common/hooks/use_column_selection_config";
import { GridSlots } from "@mui/x-data-grid";

import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { StatInformation } from "../../../../../obras/domain/models/stat_information";

/**
 * CustomToolbar
 */

const CustomToolbar = () => {
  return (
    <Toolbar>
      <Tooltip title="Colunas">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>
    </Toolbar>
  );
}

/**
 * CustomFooter
 */

const parseStat = (v: any): string => {
  if (typeof v === 'number') {
    const perc = v * 100;
    if (perc === 0) return '0,0 %';
    return `${perc.toFixed(2)} %`.replaceAll('.', ',');
  }
  return '0,0 %'
}

export const CustomFooter = ({ statInformation }: PropsFromSlot<GridSlots['footer']>) => {
  return (
    <>
      {statInformation &&
        <div className="w-full flex flex-col justify-center items-end">
          <div className="flex flex-row me-[166px]">
            <p className="flex flex-row justify-center items-center w-[8rem] py-2 bg-[#26C281] font-bold text-white">
              I.A: <span className="ps-2">{parseStat(statInformation.ia)}</span>
            </p>
            <p className="flex flex-row justify-center items-center w-[8rem] py-2 bg-[#ffff00] font-bold text-black">
              I.R: <span className="ps-2">{parseStat(statInformation.ir)}</span>
            </p>
            <p className="flex flex-row justify-center items-center w-[8rem] py-2 bg-[#ffb848] font-bold text-black">
              I.RA: <span className="ps-2">{parseStat(statInformation.ira)}</span>
            </p>
            <p className="flex flex-row justify-center items-center w-[8rem] py-2 bg-[#ef4836] font-bold text-white">
              I.REP: <span className="ps-2">{parseStat(statInformation.ix)}</span>
            </p>
          </div>
        </div>
      }
    </>
  );
}

/**
 * StyledDataGrid
 */

interface Props extends DataGridProProps {
  tableName: string;
  height?: string;
  statInformation?: StatInformation;
}

export const CustomStyledDataGrid = ({ tableName, statInformation, height = "60vh", ...props }: Props) => {
  const [
    columnsVisibilityModel,
    handleColumnsVisibilityModel,
  ] = useColumnSelectionConfig({
    tabela: tableName,
  });

  const mode = useContext(AppContext).theme
  const StyledDataGrid = useMemo(() => {
    return styled(DataGridPro)(({ theme }) => ({
      '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#aeeece',
      },
      '& .MuiDataGrid-footerContainer': {
        display: 'none',
      },
      '& .MuiDataGrid-cell': {
        display: 'flex',
        alignItems: 'center',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        wordBreak: 'normal',
        whiteSpace: 'normal',
        lineHeight: 1.5,
        color: 'black',
      },
      '& .MuiIconButton-root': {
        color: 'black',
      },
      '& .MuiDataGrid-toolbar .MuiIconButton-root': {
        color: mode === 'light' ? 'black' : 'white',
      },
      '& .MuiDataGrid-columnHeaderTitleContainerContent': {
        '& .MuiCheckbox-root': {
          color: 'black',
        },
      },
      '& .ia-header': {
        backgroundColor: '#26C281',
      },
      '& .ir-header': {
        backgroundColor: 'yellow',
        color: 'black',
      },
      '& .ira-header': {
        backgroundColor: '#FFB848',
        color: 'black',
      },
      '& .ix-header': {
        backgroundColor: '#EF4836',
      },
      backgroundColor: mode === "dark" ? "#2C2C2C" : "#FFFFFF",
    }))
  }, [mode]);


  return (
    <Box sx={{ height: height, width: '100%' }}>
      <StyledDataGrid
        showToolbar={true}
        slots={{
          toolbar: CustomToolbar,
          footer: CustomFooter,
        }}
        slotProps={{
          columnsPanel: {
            style: {
              color: mode === "dark" ? '#ffffff' : "#000000",
            }
          },
          footer: {
            statInformation,
          } as PropsFromSlot<GridSlots['footer']>,
        }}

        treeData
        getTreeDataPath={(row: any) => row.path}
        getRowClassName={(params) => {
          // Verifica se é linha pai (tem filhos na hierarquia)
          return typeof params.id === 'string' ? 'parent-row' : '';
        }}
        groupingColDef={{
          headerName: 'Ambiente',
          flex: 1,
        }}
        columnVisibilityModel={columnsVisibilityModel}
        disableColumnSorting={true}
        multipleColumnsSortingMode='always'
        onColumnVisibilityModelChange={handleColumnsVisibilityModel}
        disableRowSelectionExcludeModel={true}
        defaultGroupingExpansionDepth={-1}
        {...props}
        sx={{
          '& .parent-row': {
            backgroundColor: mode === 'dark' ? '#383838' : '#f5f5f5',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#484848' : '#eeeeee',
            },
          },
          '& .parent-row.Mui-selected': {
            backgroundColor: '#e0e0e0',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#d5d5d5',
            },
          },
          '& .MuiDataGrid-treeDataGroupingCell': {
            paddingLeft: '10px !important',
            alignItems: 'center',
            display: 'flex',
          },
          '& .MuiDataGrid-cell--withRenderer.MuiDataGrid-cell--grouped': {
            display: 'flex',
            verticalAlign: 'middle',
            alignItems: 'center',
          },
        }}
        disableColumnMenu={false}
      />
    </Box>
  );
}